import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { useAuthArgs } from './useAuthArgs';
import { getUserInfo, type UserInfo } from '../api/userClient';
import { getMenu, type MenuItem } from '../api/menuClient';

export interface UserPortalState {
  loading: boolean;
  userInfo: UserInfo | null;
  menu: MenuItem[];
  permissions: string[];
  /** True once a signed-in user has at least one navigation node (i.e. some admin/report access). */
  hasPortalAccess: boolean;
  reload: () => void;
}

/**
 * Loads the signed-in user's profile (with the permission set resolved from the SQL identity schema)
 * and their permission-filtered navigation menu. Shared by the header entry point and the admin
 * portal so both see a single, consistent view of what the user may access. Requests carry the user's
 * MSAL bearer + identity hints (resolved via useAuthArgs).
 */
export function useUserPortal(): UserPortalState {
  const { isSignedIn } = useAuth();
  const getAuthArgs = useAuthArgs();
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!isSignedIn) {
      setUserInfo(null);
      setMenu([]);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        const auth = await getAuthArgs();
        if (!auth) {
          if (!cancelled) {
            setUserInfo(null);
            setMenu([]);
          }
          return;
        }
        const [info, menuItems] = await Promise.all([getUserInfo(auth), getMenu(auth)]);
        if (cancelled) return;
        setUserInfo(info);
        setMenu(menuItems);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[useUserPortal] load failed:', err);
        if (!cancelled) {
          setUserInfo(null);
          setMenu([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, nonce, getAuthArgs]);

  return {
    loading,
    userInfo,
    menu,
    permissions: userInfo?.permissions ?? [],
    hasPortalAccess: menu.length > 0,
    reload,
  };
}
